/**
 * Snapshot of the active user's local data (guest or logged in) and its
 * transactional and idempotent import on the receiving device.
 *
 * - The snapshot is the MATERIALIZED ROWS of the user's domain tables
 *   (`USER_DATA_TABLES`). There is no need to transfer the `ps_crud` queue:
 *   when inserting through the PowerSync API, the receiver generates its OWN
 *   equivalent upload operations (full rows), so the sender's pending changes
 *   travel already materialized in its rows.
 * - Import: `user_id` is re-stamped to the receiver's user (same pattern as
 *   `sweepVisitorData`) and only rows whose `id` does not exist are inserted →
 *   re-importing is a no-op (idempotency by id).
 * - Security: the snapshot comes from outside → tables ONLY from the allowlist
 *   and columns validated against an identifier pattern before interpolating.
 */

import { USER_DATA_TABLES } from "@apunte-digital/powersync";
import { isRecord } from "./guards";

/**
 * PARENT→CHILD transfer order for apunte-digital.
 *
 * apunte has CHILD tables: `block` and `attachment` reference `page.id` via
 * `page_id`. `USER_DATA_TABLES` lists them in child-first order (alphabetical:
 * `attachment`, `block`, `page`). For the handoff we reorder PARENT-first
 * (`page` before its children) so that:
 *   (a) the transactional import inserts the consistent set without depending
 *       on arrival order, and
 *   (b) the receiver's PowerSync upload queue (if logged in) uploads `page`
 *       BEFORE `block`/`attachment`, respecting the server's FK.
 * It is derived by partitioning `USER_DATA_TABLES` (parents first, children
 * after) → it is always a permutation of it, even if the catalog changes (a
 * test verifies it). When adding a new CHILD table, add it to `CHILD_TABLES`.
 */
const CHILD_TABLES = new Set<string>(["block", "attachment"]);
export const TRANSFER_TABLES: readonly string[] = [
  ...USER_DATA_TABLES.filter((t) => !CHILD_TABLES.has(t)),
  ...USER_DATA_TABLES.filter((t) => CHILD_TABLES.has(t)),
];

/** Minimum DB surface (PowerSync satisfies it; the tests inject a fake). */
export interface SnapshotLockContext {
  getAll<T>(sql: string, params?: unknown[]): Promise<T[]>;
  execute(sql: string, params?: unknown[]): Promise<unknown>;
}

export interface SnapshotDb extends SnapshotLockContext {
  writeTransaction<T>(fn: (tx: SnapshotLockContext) => Promise<T>): Promise<T>;
}

export const SNAPSHOT_VERSION = 1;

export interface TransferSnapshot {
  version: number;
  tables: Record<string, Record<string, unknown>[]>;
}

export interface ImportCounts {
  inserted: number;
  skipped: number;
}

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export async function buildSnapshot(
  db: SnapshotLockContext,
  userId: string,
  tables: readonly string[] = TRANSFER_TABLES,
): Promise<TransferSnapshot> {
  const snapshot: TransferSnapshot = { version: SNAPSHOT_VERSION, tables: {} };
  for (const table of tables) {
    if (!IDENTIFIER_RE.test(table)) {
      throw new Error(`Invalid table name: ${table}`);
    }
    snapshot.tables[table] = await db.getAll<Record<string, unknown>>(
      `SELECT * FROM ${table} WHERE user_id = ?`,
      [userId],
    );
  }
  return snapshot;
}

export function snapshotRowCount(snapshot: TransferSnapshot): number {
  return Object.values(snapshot.tables).reduce((sum, rows) => sum + rows.length, 0);
}

export function parseSnapshotJson(json: string): TransferSnapshot | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== SNAPSHOT_VERSION || !isRecord(parsed.tables)) {
    return null;
  }
  // We rebuild the snapshot by validating each row (untrusted JSON): no type
  // assertions, only what passes the guards goes in.
  const tables: Record<string, Record<string, unknown>[]> = {};
  for (const [name, rows] of Object.entries(parsed.tables)) {
    if (!Array.isArray(rows)) {
      return null;
    }
    const validRows: Record<string, unknown>[] = [];
    const rowList: unknown[] = rows;
    for (const row of rowList) {
      if (!isRecord(row)) {
        return null;
      }
      validRows.push(row);
    }
    tables[name] = validRows;
  }
  return { version: SNAPSHOT_VERSION, tables };
}

/**
 * Imports the snapshot in a single write transaction.
 *
 * Idempotent: rows whose `id` already exists locally are skipped (local data
 * is never overwritten). Imported ones are re-stamped with `receiverUserId`,
 * so that (a) the local UI shows them to the active user and (b) if the
 * receiver is logged in, the PowerSync queue uploads them with the correct
 * owner.
 */
export async function importSnapshot(
  db: SnapshotDb,
  snapshot: TransferSnapshot,
  receiverUserId: string,
  allowedTables: readonly string[] = TRANSFER_TABLES,
): Promise<ImportCounts> {
  if (!receiverUserId) {
    throw new Error("receiverUserId is required");
  }
  const counts: ImportCounts = { inserted: 0, skipped: 0 };

  await db.writeTransaction(async (tx) => {
    // Allowlist order (parents before children), not the snapshot's.
    for (const table of allowedTables) {
      const rows = snapshot.tables[table];
      if (!rows?.length) {
        continue;
      }

      // Sender/receiver version skew: we only insert columns that exist
      // locally (PRAGMA also works on PowerSync's views). No rows or no pragma
      // → no extra filter.
      const localColumns = new Set(
        (await tx.getAll<{ name: string }>(`PRAGMA table_info(${table})`)).map((c) => c.name),
      );

      const incomingIds = rows
        .map((row) => row.id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      const existing = new Set<string>();
      const BATCH = 200;
      for (let i = 0; i < incomingIds.length; i += BATCH) {
        const batch = incomingIds.slice(i, i + BATCH);
        const placeholders = batch.map(() => "?").join(",");
        const found = await tx.getAll<{ id: string }>(
          `SELECT id FROM ${table} WHERE id IN (${placeholders})`,
          batch,
        );
        for (const row of found) {
          existing.add(row.id);
        }
      }

      for (const row of rows) {
        const { id } = row;
        if (typeof id !== "string" || id.length === 0) {
          counts.skipped += 1;
          continue;
        }
        if (existing.has(id)) {
          counts.skipped += 1;
          continue;
        }
        const stamped: Record<string, unknown> = { ...row };
        if ("user_id" in stamped) {
          stamped.user_id = receiverUserId;
        }
        const columns = Object.keys(stamped).filter(
          (c) => IDENTIFIER_RE.test(c) && (localColumns.size === 0 || localColumns.has(c)),
        );
        if (columns.length === 0) {
          counts.skipped += 1;
          continue;
        }
        const placeholders = columns.map(() => "?").join(", ");
        await tx.execute(
          `INSERT INTO ${table} (${columns.join(", ")}) VALUES (${placeholders})`,
          columns.map((c) => stamped[c] ?? null),
        );
        counts.inserted += 1;
      }
    }
  });

  return counts;
}
